import { useCallback, useState } from 'react';
import {
  createWorkspace,
  getWorkspaces,
  setWorkspacesEnabled,
  updateWorkspace,
} from '../lib/internal.js';
import { useAsyncData } from '../lib/useAsyncData.js';
import { Badge } from './ui.js';

const dollars = (cents: number | null) => (cents == null ? '—' : `$${(cents / 100).toFixed(2)}`);
const toCents = (v: string): number | null => {
  const n = Number(v);
  return v.trim() === '' || Number.isNaN(n) ? null : Math.round(n * 100);
};

/**
 * Internal workspace management for an account: toggle the multi-workspace
 * feature, create workspaces, and set each one's per-member (PMPM) rate.
 */
export function WorkspacesPanel({ accountId }: { accountId: string }) {
  const { data, error, reload } = useAsyncData(
    useCallback(() => getWorkspaces(accountId), [accountId]),
  );
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState('');
  const [newRate, setNewRate] = useState('');

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      await reload();
    } finally {
      setBusy(false);
    }
  };

  if (error) return <span className="text-xs text-rose-600">Couldn’t load workspaces</span>;
  if (!data) return <span className="text-xs text-neutral-400">Loading workspaces…</span>;

  return (
    <div className="rounded-lg border border-neutral-200/70 bg-white p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">
          Workspaces (internal)
        </div>
        <div className="flex items-center gap-2">
          <Badge tone={data.workspacesEnabled ? 'emerald' : 'neutral'}>
            {data.workspacesEnabled ? 'Multi-workspace ON' : 'Single (default)'}
          </Badge>
          <button
            type="button"
            disabled={busy}
            onClick={() => run(() => setWorkspacesEnabled(accountId, !data.workspacesEnabled))}
            className="rounded-md border border-neutral-300 px-2 py-1 text-xs font-medium text-neutral-700 transition hover:bg-neutral-100 disabled:opacity-50"
          >
            {data.workspacesEnabled ? 'Disable' : 'Enable'}
          </button>
        </div>
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-neutral-500">
            <th className="py-1">Workspace</th>
            <th className="py-1">Rate (PMPM)</th>
            <th className="py-1" />
          </tr>
        </thead>
        <tbody>
          {data.workspaces.map((w) => (
            <tr key={w.id} className="border-t border-neutral-100">
              <td className="py-1.5">
                {w.name}{' '}
                {w.isDefault && <span className="text-xs text-neutral-400">· default</span>}
              </td>
              <td className="py-1.5 tabular-nums">{dollars(w.rateCents)}</td>
              <td className="py-1.5 text-right">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    const v = prompt(
                      `Per-member rate for "${w.name}" in dollars (blank = account rate)`,
                    );
                    if (v === null) return;
                    run(() => updateWorkspace(accountId, w.id, { rateCents: toCents(v) }));
                  }}
                  className="text-xs font-medium text-emerald-700 hover:underline disabled:opacity-50"
                >
                  Set rate
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-3 flex items-center gap-2">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New workspace name"
          className="flex-1 rounded-md border border-neutral-300 px-2 py-1 text-sm"
        />
        <input
          value={newRate}
          onChange={(e) => setNewRate(e.target.value)}
          placeholder="Rate $ (optional)"
          className="w-28 rounded-md border border-neutral-300 px-2 py-1 text-sm"
        />
        <button
          type="button"
          disabled={busy || !newName.trim()}
          onClick={() =>
            run(async () => {
              await createWorkspace(accountId, {
                name: newName.trim(),
                rateCents: toCents(newRate),
              });
              setNewName('');
              setNewRate('');
            })
          }
          className="rounded-md bg-emerald-600 px-3 py-1 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-50"
        >
          Add
        </button>
      </div>
    </div>
  );
}
